import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Breadcrumbs,
  Link,
  FormControlLabel,
  Switch,
  Divider,
  ButtonGroup
} from '@mui/material';
import {
  Person as PersonIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { UsersService, User, CreateUserData } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../contexts/PermissionsContext';

/**
 * Edit User Page Component
 * Allows admins to edit user details, roles, and status
 */
const EditUserPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [canPromote, setCanPromote] = useState(false);
  const [canDemote, setCanDemote] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'client' as 'admin' | 'client' | 'project_manager' | 'project_manager_lead' | 'developer' | 'developer_lead',
    phone: '',
    is_active: true,
    password: '',
    password_confirmation: ''
  });

  // Role options
  const roleOptions = [
    { value: 'admin', label: 'Administrator' },
    { value: 'project_manager', label: 'Project Manager' },
    { value: 'project_manager_lead', label: 'Project Manager Lead' },
    { value: 'developer', label: 'Developer' },
    { value: 'developer_lead', label: 'Developer Lead' },
    { value: 'client', label: 'Client' }
  ];

  // Load user data
  const loadUserData = async () => {
    if (!userId) {
      setError('Invalid user ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const userData = await UsersService.getUser(parseInt(userId));
      setUser(userData);
      
      // Populate form with existing user data
      setFormData({
        name: userData.name,
        email: userData.email,
        role: userData.role as 'admin' | 'client' | 'project_manager' | 'project_manager_lead' | 'developer' | 'developer_lead',
        phone: userData.phone || '',
        is_active: userData.is_active,
        password: '',
        password_confirmation: ''
      });
      
    } catch (err) {
      console.error('Error loading user:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await loadUserData();
      await loadPromotionStatus();
    };
    
    loadData();
  }, [userId]);

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear messages when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userId || !user) {
      setError('Invalid user data');
      return;
    }

    // Validate form
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    // If password is provided, validate confirmation
    if (formData.password && formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      // Prepare update data
      const updateData: Partial<CreateUserData> = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role,
        phone: formData.phone?.trim() || undefined,
        is_active: formData.is_active
      };

      // Only include password if it's being changed
      if (formData.password) {
        updateData.password = formData.password;
        updateData.password_confirmation = formData.password_confirmation;
      }

      // Update user
      const updatedUser = await UsersService.updateUser(parseInt(userId), updateData);
      
      setUser(updatedUser);
      setSuccess('User updated successfully!');
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        password: '',
        password_confirmation: ''
      }));

    } catch (err: any) {
      console.error('Error updating user:', err);
      
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.errors) {
        const errors = Object.values(err.response.data.errors).flat() as string[];
        setError(errors.join(', '));
      } else {
        setError('Failed to update user. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Load promotion status
  const loadPromotionStatus = async () => {
    if (!userId) return;
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/promotion-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Accept': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCanPromote(data.data.can_promote);
        setCanDemote(data.data.can_demote);
      }
    } catch (err) {
      console.error('Error loading promotion status:', err);
    }
  };

  // Handle promotion
  const handlePromotion = async () => {
    if (!userId) return;
    
    try {
      setPromotionLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/promote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message);
        await loadUserData(); // Reload user data
        await loadPromotionStatus(); // Reload promotion status
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to promote user');
      }
    } catch (err) {
      setError('Failed to promote user');
    } finally {
      setPromotionLoading(false);
    }
  };

  // Handle demotion
  const handleDemotion = async () => {
    if (!userId) return;
    
    try {
      setPromotionLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/demote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message);
        await loadUserData(); // Reload user data
        await loadPromotionStatus(); // Reload promotion status
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to demote user');
      }
    } catch (err) {
      setError('Failed to demote user');
    } finally {
      setPromotionLoading(false);
    }
  };

  // Check if permissions are still loading
  if (permissionsLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading permissions...</Typography>
        </Box>
      </Container>
    );
  }

  // Check permissions
  if (!hasPermission('user.edit')) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          You don't have permission to edit users.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          User not found.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/admin/users')}
          sx={{ mt: 2 }}
        >
          Back to Users
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/dashboard/admin" color="inherit">
            Dashboard
          </Link>
          <Link component={RouterLink} to="/dashboard/admin/users" color="inherit">
            Users
          </Link>
          <Typography color="text.primary">Edit User</Typography>
        </Breadcrumbs>
        
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
          <PersonIcon color="primary" sx={{ fontSize: 32 }} />
          <div>
            <Typography variant="h4" component="h1">
              Edit User
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {user.name} ({user.email})
            </Typography>
          </div>
        </Box>

        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/admin/users')}
        >
          Back to Users
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Edit Form */}
      <Paper elevation={1}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
            <EditIcon color="primary" />
            <Typography variant="h6">User Information</Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              {/* Name */}
              <TextField
                label="Full Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                fullWidth
              />

              {/* Email */}
              <TextField
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                fullWidth
              />

              {/* Role */}
              <TextField
                select
                label="Role"
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                required
                fullWidth
              >
                {roleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {/* Phone */}
              <TextField
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                fullWidth
              />
            </Box>

            {/* Active Status */}
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                }
                label="Account Active"
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Password Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Change Password (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Leave blank to keep current password
            </Typography>

            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              <TextField
                label="New Password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                fullWidth
              />

              <TextField
                label="Confirm New Password"
                type="password"
                value={formData.password_confirmation}
                onChange={(e) => handleInputChange('password_confirmation', e.target.value)}
                fullWidth
              />
            </Box>

            {/* Submit Button */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                disabled={submitting}
                sx={{ minWidth: 120 }}
              >
                {submitting ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/dashboard/admin/users')}
                disabled={submitting}
              >
                Cancel
              </Button>

              {/* Promotion/Demotion Buttons */}
              {(canPromote || canDemote) && (
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                  {canPromote && (
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      startIcon={<TrendingUpIcon />}
                      onClick={handlePromotion}
                      disabled={promotionLoading || submitting}
                      sx={{ minWidth: 120 }}
                    >
                      {promotionLoading ? <CircularProgress size={20} /> : 'Promote to Lead'}
                    </Button>
                  )}
                  
                  {canDemote && (
                    <Button
                      variant="contained"
                      color="warning"
                      size="large"
                      startIcon={<TrendingDownIcon />}
                      onClick={handleDemotion}
                      disabled={promotionLoading || submitting}
                      sx={{ minWidth: 120 }}
                    >
                      {promotionLoading ? <CircularProgress size={20} /> : 'Demote from Lead'}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Paper>
    </Container>
  );
};

export default EditUserPage;